/**
 * Reader-facing copy for a failed dashboard write.
 *
 * `services/common.request` builds its message from the transport: a 500 arrives
 * as "Network error received (status code 500): <server message>", and a 401
 * arrives either as that same shape or as "You are not logged in." from the
 * pre-flight token check. Both were passed straight into the save toast, so a
 * business reader saw an HTTP status code, and in the 404/403 cases a phrase
 * ("Lens not found") that names an internal object rather than telling them
 * what to do. Every branch here states the situation and gives exactly one
 * recovery action; the caller keeps the dialog open so the action is available.
 */

type WriteAction = 'add' | 'save' | 'create';

const SIGNED_OUT_HINT = /not logged in|not authenticated|no access token/i;

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

function codeOf(error: unknown): string | undefined {
  const body = (error as { responseBody?: unknown } | null)?.responseBody;
  if (!body || typeof body !== 'object') return undefined;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/** True when this failure means the reader has to sign in again before retrying. */
export function isLensWriteSignedOut(error: unknown): boolean {
  if (statusOf(error) === 401) return true;
  const message = error instanceof Error ? error.message : '';
  return SIGNED_OUT_HINT.test(message);
}

/**
 * One plain sentence plus one recovery action, chosen from the status the
 * service returned. Anything unrecognised falls through to the retry wording:
 * the dialog still holds the destination, title, visual and SQL, so retrying is
 * always a true instruction even when the cause is unknown.
 */
export function lensWriteFailureMessage(error: unknown, action: WriteAction = 'add'): string {
  const doing =
    action === 'add'
      ? 'The card was not added'
      : action === 'create'
        ? 'The Lens was not created'
        : 'Your change was not saved';

  if (isLensWriteSignedOut(error)) {
    return `${doing} because you have been signed out. Sign in again in a new tab, then use the same button to try again.`;
  }

  // The card cap is the one 400 a reader can act on precisely, so the service
  // labels it rather than leaving it inside the validator's schema wording.
  if (codeOf(error) === 'dashboard_full') {
    return `${doing} because this Lens already holds the maximum of 24 cards. Choose another Lens, or remove a card from this one first.`;
  }

  switch (statusOf(error)) {
    // The service refuses a write built from a version another tab has since replaced. The
    // hooks reload the list the dialog reads its destination from, so the retry does carry the
    // new version; the sentence has to say the dashboard was reached and changed, not that it
    // was unreachable, or the reader is told something false about a save that was refused.
    case 409:
      return `${doing} because this Lens changed since you opened it. It has been reloaded, so use the same button to try again.`;
    case 403:
      return `${doing}. You can read this Lens but not change it. Choose a Lens you own, or ask its owner to add it for you.`;
    case 404:
      return `${doing} because that Lens is no longer available. Choose another Lens from the list.`;
    case 413:
      return `${doing} because this Lens is too large to save. Reduce its content and try again.`;
    case 422:
    case 400:
      return `${doing} because the Lens contains an unsupported setting. Your last saved version is unchanged. Reload the Lens; if it still fails, report this error.`;
    default:
      return `${doing} because the Lens could not be reached. Nothing has changed. Your Lens, card title and visual are still here, so try again.`;
  }
}
